import { Ticket } from '../interfaces/ticket.interface';
import { CustomPropertiesAnswerJsonKey, FilterDataType } from '../utils/constants';

// Board view filters its tickets in memory (see customizedFilter), so custom property answers have to
// be matched here the way the server matches the list view's `cpfilter` query
// (CustomPropertyExtensions.ApplyCustomProperyFilters). Match what the server *does* with a filter, not
// the operator the list view puts on the wire - the two differ for strings, which is bug 35236.
// Answers live on the ticket as a JSON blob keyed by PropertyId.
//
// Each filter is compiled into a matcher once, up front, so the per-ticket pass only does a JSON parse
// and a set lookup rather than re-deriving the picked value for every ticket on the board.
const customPropertyKeyPrefix = `${CustomPropertiesAnswerJsonKey}.`;

type AnswerMatcher = (answer: any) => boolean;

const matchEverything: AnswerMatcher = () => true;

// Deliberately not a truthiness check: a picked number of 0, or an answer of "0"/false, is a value.
// Only null, undefined and the empty string a cleared input leaves behind count as absent.
const hasValue = (value: any) => value !== null && value !== undefined && value.toString().length > 0;

const isCustomPropertyFilter = (filter: FilterPropsItems) => filter?.key?.startsWith(customPropertyKeyPrefix) ?? false;

const isCustomPropertyFilterApplied = (filter: FilterPropsItems) =>
  filter.FilterDataType === FilterDataType.String
    ? !!filter.stringValue
    : filter.values?.some(value => value.selected) ?? false;

const parseCustomPropertyAnswers = (ticket: Ticket) => {
  const answers = ticket?.CustomPropertiesAnswerJson;
  if ((answers?.length ?? 0) === 0) return {};
  try {
    return JSON.parse(answers) ?? {};
  } catch {
    return {};
  }
};

const toTimestamp = (value: any) => {
  const timestamp = new Date(value).getTime();
  return isNaN(timestamp) ? null : timestamp;
};

const toEndOfDayTimestamp = (value: any) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  date.setHours(23, 59, 59, 999);
  return date.getTime();
};

// A date/time property is only queried once a date has been picked, so an operator on its own leaves
// the ticket set untouched - the list view skips the filter entirely in that case.
const buildDateMatcher = (selected: FilterAccordionValues[], isTimeOnly: boolean): AnswerMatcher => {
  const pickedDate = selected.find(value => hasValue(value.date))?.date;
  const startOfPickedDate = pickedDate === undefined ? null : toTimestamp(pickedDate);
  if (startOfPickedDate === null) return matchEverything;

  // Times are compared as stored; only dates widen "earlier/later than" to the whole picked day.
  const endOfPickedDate = isTimeOnly ? startOfPickedDate : toEndOfDayTimestamp(pickedDate);
  const operators = selected.map(({ key }) => key);

  return answer => {
    const answerTimestamp = hasValue(answer) ? toTimestamp(answer) : null;
    if (answerTimestamp === null) return false;

    return operators.some(operator => {
      switch (operator) {
        case 0: return answerTimestamp < startOfPickedDate;
        case 1: return answerTimestamp <= endOfPickedDate;
        case 2: return answerTimestamp > endOfPickedDate;
        case 3: return answerTimestamp >= startOfPickedDate;
        default: return false;
      }
    });
  };
};

const buildNumberMatcher = (selected: FilterAccordionValues[]): AnswerMatcher => {
  const pickedNumber = selected.find(value => hasValue(value.number))?.number;
  const pickedValue = pickedNumber === undefined ? NaN : Number(pickedNumber);
  if (isNaN(pickedValue)) return matchEverything;

  const operators = selected.map(({ key }) => key);

  return answer => {
    if (!hasValue(answer)) return false;
    const answerNumber = Number(answer);
    if (isNaN(answerNumber)) return false;

    return operators.some(operator => {
      switch (operator) {
        case 0: return answerNumber === pickedValue;
        case 1: return answerNumber !== pickedValue;
        case 2: return answerNumber < pickedValue;
        case 3: return answerNumber <= pickedValue;
        case 4: return answerNumber > pickedValue;
        case 5: return answerNumber >= pickedValue;
        default: return false;
      }
    });
  };
};

// Key 1 is the "True" option and key 2 the "False" one, per the boolean filter built in Tickets.
const buildBooleanMatcher = (selected: FilterAccordionValues[]): AnswerMatcher => {
  const matchesTrue = selected.some(({ key }) => key === 1);
  const matchesFalse = selected.some(({ key }) => key === 2);

  return answer =>
    hasValue(answer) && (answer.toString().toLowerCase() === 'true' ? matchesTrue : matchesFalse);
};

// List answers hold the selected CustomPropertyListItem ids, comma separated when multiple are allowed.
const buildListMatcher = (selected: FilterAccordionValues[]): AnswerMatcher => {
  const selectedIds = new Set(selected.map(({ key }) => key?.toString()));

  return answer =>
    hasValue(answer) && answer.toString().split(',').some(id => selectedIds.has(id.trim()));
};

// The server ignores the `eq` the list view sends for a string property and always matches on a
// substring, so a partial word typed into the filter has to match here too. Comparing for equality
// instead left the board empty whenever the answer was longer than what was typed.
const buildStringMatcher = (stringValue: string): AnswerMatcher => {
  const expected = stringValue.toLowerCase();
  return answer => hasValue(answer) && answer.toString().toLowerCase().includes(expected);
};

const buildCustomPropertyMatcher = (filter: FilterPropsItems) => {
  const propertyId = filter.key.substring(customPropertyKeyPrefix.length);
  const selected = filter.values?.filter(value => value.selected) ?? [];

  let matchesAnswer: AnswerMatcher;
  switch (filter.FilterDataType) {
    case FilterDataType.String:
      matchesAnswer = buildStringMatcher(filter.stringValue);
      break;
    case FilterDataType.Date:
    case FilterDataType.Time:
      matchesAnswer = buildDateMatcher(selected, filter.FilterDataType === FilterDataType.Time);
      break;
    case FilterDataType.Number:
      matchesAnswer = buildNumberMatcher(selected);
      break;
    case FilterDataType.Boolean:
      matchesAnswer = buildBooleanMatcher(selected);
      break;
    default:
      matchesAnswer = buildListMatcher(selected);
      break;
  }

  return (answers: any) => matchesAnswer(answers?.[propertyId]);
};

// Scalar Ticket columns offered as number filters. Not custom properties - the value is read
// straight off the ticket rather than out of the answers blob - but the operators, the input and
// the comparison are identical, so buildNumberMatcher is shared rather than reimplemented.
//
// The key of such a filter IS the column name, which is what makes both halves work without a
// lookup: the list view drops it into the OData verbatim, and this reads it off the ticket.
const scalarNumberFilterFields = ['TotalTimeSpentMinutes'];

export const isScalarNumberFilter = (filter: FilterPropsItems) =>
  scalarNumberFilterFields.includes(filter?.key);

export const filterTicketsByScalarNumbers = (tickets: Ticket[], selectedValues: FilterPropsItems[]) => {
  const matchers = (selectedValues ?? [])
    .filter(filter => isScalarNumberFilter(filter) && (filter.values?.some(value => value.selected) ?? false))
    .map(filter => {
      const matchesValue = buildNumberMatcher(filter.values.filter(value => value.selected));
      return (ticket: Ticket) => matchesValue(ticket?.[filter.key]);
    });
  if (matchers.length === 0) return tickets;

  return tickets?.filter(ticket => matchers.every(matches => matches(ticket))) ?? [];
};

export const filterTicketsByCustomProperties = (tickets: Ticket[], selectedValues: FilterPropsItems[]) => {
  const matchers = (selectedValues ?? [])
    .filter(filter => isCustomPropertyFilter(filter) && isCustomPropertyFilterApplied(filter))
    .map(buildCustomPropertyMatcher);
  if (matchers.length === 0) return tickets;

  return tickets?.filter(ticket => {
    const answers = parseCustomPropertyAnswers(ticket);
    return matchers.every(matches => matches(answers));
  }) ?? [];
};
